import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import { useLocation } from 'react-use';

import { FilterState } from '../components/calendar-filters';
import { CalendarFilters } from './useCalendarData';
import {
  EXPERIMENTS_PARAM_PAGENO_KEY,
  EXPERIMENTS_PARAM_PAGESIZE_KEY,
  EXPERIMENTS_PARAM_SEARCH_KEY,
} from '@/common/const';
import { getDomainFamily } from '@/common/domains';
import { COMPLETED, REQUESTED, SCHEDULED, STARTED } from '@/constants';
import { useAppNavigate } from '@/utils/navigation';
import {
  extractFiltersFromSearchParams,
  extractParamValueByName,
  getUrlWithParams,
} from '@/utils/url';

export function useCalendarFilters() {
  const { navigateTo } = useAppNavigate();
  const location = useLocation();
  const [searchVal, setSearchVal] = useState<string>('');
  const [filters, setFilters] = useState<FilterState>({});
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>();

  const navigateRef = useRef(navigateTo);
  useEffect(() => {
    navigateRef.current = navigateTo;
  }, [navigateTo]);

  const debouncedFilter = useMemo(
    () =>
      debounce(
        (url: string) => navigateRef.current(url, { replace: true }),
        500,
      ),
    [],
  );

  const updateURL = useCallback(
    (payload: FilterState) => {
      debouncedFilter(
        getUrlWithParams(`/${getDomainFamily()}/experiments`, payload),
      );
    },
    [debouncedFilter],
  );

  const extractFiltersFromURL = useCallback(() => {
    const searchUrl = location.search;
    const searchValue =
      extractParamValueByName(searchUrl, EXPERIMENTS_PARAM_SEARCH_KEY) || '';
    const blacklist = [
      EXPERIMENTS_PARAM_SEARCH_KEY,
      EXPERIMENTS_PARAM_PAGENO_KEY,
      EXPERIMENTS_PARAM_PAGESIZE_KEY,
      'env',
    ];
    const urlFilters = extractFiltersFromSearchParams(searchUrl, blacklist);

    setSearchVal(searchValue);
    setFilters(urlFilters);

    return { searchValue, urlFilters };
  }, [location.search]);

  const buildCalendarFilters = useCallback(
    (dates?: dayjs.Dayjs[]): CalendarFilters => {
      let payload: CalendarFilters = {
        ...filters,
        offset: 0,
        limit: 0,
      };

      // Default state filters if none selected
      if (!filters?.['state']?.length) {
        payload['state'] = [REQUESTED, SCHEDULED, STARTED, COMPLETED];
      }

      if (searchVal) {
        payload['name'] = searchVal;
      }

      if (dates?.length) {
        payload['dates'] = dates;
      }

      return payload;
    },
    [filters, searchVal],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      const payload = {
        ...filters,
        name: searchValue,
      };
      updateURL(payload);
    },
    [filters, updateURL],
  );

  const handleApplyFilter = useCallback(
    (newFilters: FilterState) => {
      updateURL(newFilters);
    },
    [updateURL],
  );

  const handleDateRangeChange = useCallback(
    (dates: [dayjs.Dayjs, dayjs.Dayjs]) => {
      setDateRange(dates);
    },
    [],
  );

  // Extract filters when URL changes
  useEffect(() => {
    extractFiltersFromURL();
  }, [extractFiltersFromURL]);

  return {
    searchVal,
    setSearchVal,
    filters,
    dateRange,
    buildCalendarFilters,
    handleSearch,
    handleApplyFilter,
    handleDateRangeChange,
  };
}
